import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Target, Eye, Award, Users, Building2, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO, { SEO_DATA } from "@/components/SEO";

const ABOUT_IMAGE = "https://images.unsplash.com/photo-1758518727707-b023e285b709?crop=entropy&cs=srgb&fm=jpg&q=85";

const AboutPage = () => {
  const { t } = useTranslation();
  
  const values = [
    {
      icon: ShieldCheck,
      title: t('about.values.integrity'),
      description: t('about.values.integrityDesc')
    },
    {
      icon: Award,
      title: t('about.values.excellence'),
      description: t('about.values.excellenceDesc')
    },
    {
      icon: Users,
      title: t('about.values.commitment'),
      description: t('about.values.commitmentDesc')
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="about-page">
      <SEO 
        title={SEO_DATA.about.title}
        description={SEO_DATA.about.description}
        keywords={SEO_DATA.about.keywords}
        url="/nosotros"
      />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="text-[#D4AF37] text-sm uppercase tracking-widest">
                {t('about.label')}
              </span>
              <h1 className="mt-4 text-4xl md:text-5xl text-white leading-tight">
                {t('about.heroTitle')}
                <span className="text-[#D4AF37]"> {t('about.heroHighlight')}</span>
              </h1>
              <p className="mt-6 text-lg text-[#A3A3A3] leading-relaxed">
                {t('about.description1')}
              </p>
              <p className="mt-4 text-lg text-[#A3A3A3] leading-relaxed">
                {t('about.description2')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src={ABOUT_IMAGE}
                  alt="Equipo Ikonic Deals"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative frame */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-[#D4AF37]/30 -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 bg-[#0a0a0a]">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 md:p-12 bg-[#121212] border border-white/5"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#D4AF37]/10 mb-6">
                <Target size={28} className="text-[#D4AF37]" />
              </div>
              <h2 className="text-2xl text-white mb-4">{t('about.mission.title')}</h2>
              <p className="text-[#A3A3A3] leading-relaxed">
                {t('about.mission.text')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="p-8 md:p-12 bg-[#121212] border border-white/5"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#D4AF37]/10 mb-6">
                <Eye size={28} className="text-[#D4AF37]" />
              </div>
              <h2 className="text-2xl text-white mb-4">{t('about.vision.title')}</h2>
              <p className="text-[#A3A3A3] leading-relaxed">
                {t('about.vision.text')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest">
              {t('about.valuesLabel')}
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl text-white">
              {t('about.values.title')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="text-center p-8 bg-[#121212]/50 border border-white/5 hover:border-[#D4AF37]/30 transition-all duration-500"
              >
                <div className="w-16 h-16 mx-auto flex items-center justify-center bg-[#D4AF37]/10 mb-6">
                  <value.icon size={32} className="text-[#D4AF37]" />
                </div>
                <h3 className="text-xl text-white mb-4">{value.title}</h3>
                <p className="text-[#A3A3A3] leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 md:py-24 bg-[#0a0a0a] border-y border-white/5">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "10+", label: "Años de Experiencia" },
              { number: "150+", label: "Propiedades Vendidas" },
              { number: "200+", label: "Clientes Satisfechos" },
              { number: "$50M+", label: "En Transacciones" },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <p className="stat-number">{stat.number}</p>
                <p className="stat-label">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest">
              ¿Por Qué Elegirnos?
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl text-white">
              La Diferencia Ikonic Deals
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: Building2,
                title: "Portafolio Exclusivo",
                description: "Acceso a propiedades únicas no disponibles en el mercado general, desde residencias de lujo hasta oportunidades de inversión."
              },
              {
                icon: Users,
                title: "Atención Personalizada",
                description: "Cada cliente recibe un asesor dedicado que entiende sus necesidades específicas y trabaja incansablemente para cumplirlas."
              },
              {
                icon: ShieldCheck,
                title: "Proceso Seguro",
                description: "Garantizamos transacciones transparentes con documentación legal verificada y acompañamiento en cada paso del proceso."
              },
              {
                icon: Award,
                title: "Resultados Comprobados",
                description: "Nuestro historial de éxito habla por sí solo, con cientos de transacciones exitosas y clientes satisfechos."
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-6 p-6 bg-[#121212]/30 border border-white/5 hover:border-[#D4AF37]/20 transition-all"
              >
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-[#D4AF37]/10">
                  <item.icon size={24} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="text-lg text-white mb-2">{item.title}</h3>
                  <p className="text-[#A3A3A3] text-sm leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
